import { z } from 'zod';
import { dataHoraSchema, paginacaoSchema } from './comum.js';
/**
 * ============================================================================
 * FILA DE TRABALHO
 * ============================================================================
 *
 * Hoje a geracao em massa e a varredura de campanha rodam DENTRO do navegador,
 * num laco de setTimeout. Fechou a aba, perdeu o lote; duas abas abertas fazem o
 * mesmo trabalho duas vezes; nao ha retentativa nem registro do que ficou pela
 * metade.
 *
 * A fila fica em Postgres, nao em Redis. Decisao da secao 14 do diagnostico: neste
 * volume o Postgres basta e evita mais uma peca para operar. Se aparecer contencao
 * medida, trocar por BullMQ e barato — o contrato aqui nao muda.
 */
export const tipoJobSchema = z.enum([
    /** Gera o spec de um site com IA a partir dos dados do lead. */
    'gerar_site',
    /** Renderiza o spec, sobe o HTML para o R2 e vira o ponteiro da versao. */
    'publicar_site',
    /** Um passo de campanha: varre uma regiao por um alvo. */
    'varrer_regiao',
    /** Consulta o WhatsApp de um lead. */
    'enriquecer_lead',
    /** Mensagem para um lead. Concorrencia 1 por instancia, com cota diaria. */
    'disparar_whatsapp',
    /** Migracao: extrai base64 do HTML antigo para o R2 e reescreve as URLs. */
    'extrair_imagens_base64',
    /** Migracao: sobe um site legado para o R2 como htmlBruto. */
    'backfill_site_r2',
    /**
     * Importacao em massa: um site exportado por ferramenta externa.
     *
     * Um job por SITE, e nao um por lote. E o que da retentativa isolada (um ZIP
     * corrompido no meio de cinco mil nao derruba os outros 4999), progresso pela
     * propria fila e lease curto: processar um site cabe folgado nos 300 s de
     * lease, processar cinco mil nao caberia.
     */
    'importar_site_externo',
]);
export const statusJobSchema = z.enum(['pendente', 'processando', 'concluido', 'falhou']);
export const jobSchema = z.object({
    id: z.string(),
    tipo: tipoJobSchema,
    /**
     * Chave natural do TRABALHO, nao do job. O UNIQUE aqui e o que impede o mesmo
     * disparo de sair duas vezes quando o usuario clica duas vezes ou um retry de
     * rede reenvia o pedido. Formato: `<tipo>:<chave natural>`.
     */
    chaveIdempotencia: z.string().nullable(),
    payload: z.unknown(),
    status: statusJobSchema,
    tentativas: z.number().int(),
    maxTentativas: z.number().int(),
    rodarApos: dataHoraSchema,
    /** Recurso cuja cota o job consome: instancia de WhatsApp, chave do Gemini, etc. */
    recurso: z.string().nullable(),
    ultimoErro: z.string().nullable(),
    criadoEm: dataHoraSchema,
    atualizadoEm: dataHoraSchema,
});
/**
 * ============================================================================
 * O PAYLOAD DA IMPORTACAO EM MASSA
 * ============================================================================
 *
 * O QUE NAO ESTA AQUI E O PONTO
 *
 * Nao ha HTML nem base64 neste payload — so CHAVES do R2. O export externo traz
 * um `index.html` de ~820 KB do qual 92% e imagem em base64, mais um
 * `data.json` de ~430 KB com as mesmas imagens de novo. Enfiar isso no
 * `payload` jsonb seria ~1,2 MB por linha: em cinco mil sites, 6 GB numa tabela
 * de fila, reescritos a cada update de status. E exatamente o que a secao 08 do
 * diagnostico proibe ("HTML e imagem nunca no banco").
 *
 * Entao o navegador sobe os arquivos DIRETO para o R2 com URL pre-assinada, e o
 * job carrega so o endereco deles. O payload fica em centenas de bytes.
 */
export const importarSiteExternoSchema = z.object({
    /** Identifica o lote na tela e nos logs. Nao existe no banco: e so rotulo. */
    lote: z.string().min(1).max(120),
    /** A pasta dentro do ZIP, que e o nome natural do site no export. */
    pasta: z.string().min(1).max(200),
    /** Chave no R2 do `index.html` cru, com os base64 ainda dentro. */
    chaveHtml: z.string().min(1),
    /** Chave no R2 do `data.json`. Ausente quando o export nao trouxe. */
    chaveDados: z.string().min(1).optional(),
    /** Cadastro minimo, lido do `system_meta.json` e do `data.json` no navegador. */
    cliente: z.object({
        nome: z.string().min(1),
        slug: z.string().min(1),
        categoria: z.string().optional(),
        telefone: z.string().optional(),
        endereco: z.string().optional(),
        site: z.string().optional(),
        nota: z.number().optional(),
        totalAvaliacoes: z.number().int().optional(),
    }),
});
/**
 * ============================================================================
 * O PAYLOAD DE `gerar_site`
 * ============================================================================
 *
 * Estava escrito duas vezes: um `z.object` anonimo dentro do manipulador do
 * worker e um objeto montado a mao em cada chamador. Ganhou nome quando a
 * criacao em massa acrescentou `lote` — campo que o worker precisa REPASSAR para
 * `ia.gerarSite`, e um campo que o worker nao conhece e um campo que se perde em
 * silencio: o job rodaria, a IA geraria, e o gasto ficaria sem lote nenhum.
 */
export const gerarSitePayloadSchema = z.object({
    clienteId: z.uuid(),
    slug: z.string().optional(),
    /** Forca um template. Ausente = escolha automatica pela categoria. */
    templateId: z.string().optional(),
    /** Valores editados no painel. Ausente = derivados do cadastro. */
    valores: z.record(z.string(), z.string()).optional(),
    /** Forca o caminho deste job. Ausente = o modo configurado no servidor. */
    modo: z.enum(['ia', 'template']).optional(),
    /**
     * O lote da criacao em massa, quando o job veio de um.
     *
     * So rotulo — nao existe tabela de lote. Serve para `uso_ia.lote`, e e por ele
     * que a tela do lote soma o gasto real do envio.
     */
    lote: z.string().min(1).max(120).optional(),
});
export const enfileirarSchema = z.object({
    tipo: tipoJobSchema,
    payload: z.unknown().default({}),
    chaveIdempotencia: z.string().max(300).nullable().optional(),
    recurso: z.string().max(120).nullable().optional(),
    maxTentativas: z.number().int().min(1).max(20).default(5),
    /** Atraso inicial em segundos. Serve para agendar disparo. */
    atrasoSegundos: z.number().int().min(0).max(60 * 60 * 24 * 30).default(0),
});
/**
 * Enfileirar MUITOS de uma vez.
 *
 * Cinco mil sites eram cinco mil POSTs, cada um com sua ida e volta de rede e,
 * com `chaveIdempotencia`, duas consultas ao banco. Do navegador isso e a tela
 * travada por minutos; e a primeira falha de rede no meio deixa o lote pela
 * metade sem ninguem saber onde parou.
 *
 * O teto de 500 e o mesmo de `clientes.importar`, pela mesma razao: cabe no
 * limite de corpo da API com folga e mantem a transacao curta.
 */
export const enfileirarLoteSchema = z.object({
    jobs: z.array(enfileirarSchema).min(1).max(500),
});
export const resultadoEnfileirarLoteSchema = z.object({
    /** Quantos jobs nasceram agora. */
    enfileirados: z.number().int(),
    /** Quantos ja existiam pela chave de idempotencia. */
    jaExistiam: z.number().int(),
});
export const resultadoEnfileirarSchema = z.object({
    id: z.string(),
    /** false quando a chave de idempotencia ja existia: nada foi enfileirado. */
    novo: z.boolean(),
});
export const filtroJobsSchema = paginacaoSchema.extend({
    tipo: tipoJobSchema.optional(),
    status: statusJobSchema.optional(),
});
export const resumoFilaSchema = z.object({
    /**
     * `partialRecord`, e nao `record`.
     *
     * Em Zod 4, `z.record(enum, valor)` e EXAUSTIVO: exige toda chave do enum.
     * Mas o `groupBy` do Postgres so devolve status que EXISTEM na tabela — numa
     * fila vazia `porStatus` e `{}`, e numa fila so com falhas vem so `falhou`.
     *
     * O resultado era a resposta da API sendo recusada pelo proprio contrato, do
     * lado do navegador: "porStatus.pendente: Invalid input: expected number,
     * received undefined". A tela de importacao nao conseguia ler o andamento, e
     * o erro apontava para o contrato em vez de para a causa.
     *
     * Passou despercebido porque as verificacoes de ponta a ponta chamavam a API
     * com `fetch` cru, que nao valida a resposta. So o cliente tipado valida — ou
     * seja, so o navegador. Vale para `porTipoEStatus` tambem: aquele ja era
     * `z.record(z.string(), ...)`, que nao tem chave obrigatoria.
     */
    porStatus: z.partialRecord(statusJobSchema, z.number().int()),
    porTipo: z.record(z.string(), z.number().int()),
    /**
     * O cruzamento tipo x status — sem ele nao existe barra de progresso.
     *
     * `porStatus` e `porTipo` sao contagens INDEPENDENTES: davam para dizer
     * "3.200 jobs do tipo importacao" e "1.100 concluidos" no total da fila, e
     * nunca "1.100 das 3.200 importacoes concluidas". Numa importacao de cinco
     * mil sites, esse cruzamento e exatamente o numero que a tela precisa
     * mostrar.
     *
     * Chave `"<tipo>:<status>"`, porque objeto aninhado em `z.record` duplo fica
     * pior de consumir do que uma chave composta.
     */
    porTipoEStatus: z.record(z.string(), z.number().int()).default({}),
    deadLetter: z.number().int(),
});
//# sourceMappingURL=fila.js.map